import { FC, useContext, useEffect, useRef, useState } from 'react'
import { ChatbotUIContext } from '@/context/context'
import { Tables } from '@/supabase/types'

import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Label } from '../ui/label'
import { TextareaAutosize } from '../ui/textarea-autosize'
import { usePromptAndCommand } from './chat-hooks/use-prompt-and-command'
import { IconPlus, IconSparkle2 } from '@tabler/icons-react'

interface PromptPickerProps {}

export const PromptPicker: FC<PromptPickerProps> = ({}) => {
  const {
    prompts,
    isPromptPickerOpen,
    setIsPromptPickerOpen,
    focusPrompt,
    slashCommand,
    setPromptVariables,
    promptVariables,
    showPromptVariables,
    setShowPromptVariables,
    setIsCreatePromptOpen
  } = useContext(ChatbotUIContext)

  const { handleSelectPrompt, handleSelectPromptWithVariables } = usePromptAndCommand()

  const itemsRef = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (focusPrompt && itemsRef.current[0]) {
      itemsRef.current[0].focus()
    }
  }, [focusPrompt])

  const [isTyping, setIsTyping] = useState(false)

  const filteredPrompts = prompts.filter((prompt) =>
    prompt.name.toLowerCase().includes(slashCommand.toLowerCase())
  )

  const handleOpenChange = (isOpen: boolean) => {
    setIsPromptPickerOpen(isOpen)
  }

  const getKeyDownHandler = (index: number) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      handleOpenChange(false)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleSelectPromptWithVariables(filteredPrompts[index])
    } else if (
      (e.key === 'Tab' || e.key === 'ArrowDown') &&
      !e.shiftKey &&
      index === filteredPrompts.length - 1
    ) {
      e.preventDefault()
      itemsRef.current[0]?.focus()
    } else if (e.key === 'ArrowUp' && !e.shiftKey && index === 0) {
      // go to last element if arrow up is pressed on first element
      e.preventDefault()
      itemsRef.current[itemsRef.current.length - 1]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prevIndex = index - 1 >= 0 ? index - 1 : itemsRef.current.length - 1
      itemsRef.current[prevIndex]?.focus()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      const nextIndex = index + 1 < itemsRef.current.length ? index + 1 : 0
      itemsRef.current[nextIndex]?.focus()
    }
  }

  const handleSubmitPromptVariables = () => {
    const newPromptContent = promptVariables.reduce(
      (prevContent, variable) =>
        prevContent.replace(new RegExp(`\\{\\{${variable.name}\\}\\}`, 'g'), variable.value),
      prompts.find((prompt) => prompt.id === promptVariables[0].promptId)?.content || ''
    )

    const newPrompt: any = {
      ...prompts.find((prompt) => prompt.id === promptVariables[0].promptId),
      content: newPromptContent
    }

    handleSelectPrompt(newPrompt)
    handleOpenChange(false)
    setShowPromptVariables(false)
    setPromptVariables([])
  }

  const handleCancelPromptVariables = () => {
    setShowPromptVariables(false)
    setPromptVariables([])
  }

  const handleKeydownPromptVariables = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isTyping && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmitPromptVariables()
    }
  }

  const handleCreatePrompt = () => {
    setIsPromptPickerOpen(false)
    setIsCreatePromptOpen(true)
  }

  return (
    <>
      <Dialog open={showPromptVariables} onOpenChange={setShowPromptVariables}>
        <DialogContent onKeyDown={handleKeydownPromptVariables}>
          <DialogHeader>
            <DialogTitle>Enter Prompt Variables</DialogTitle>
          </DialogHeader>

          <div className="mt-2 space-y-6">
            {promptVariables.map((variable, index) => (
              <div key={index} className="flex flex-col space-y-2">
                <Label>{variable.name}</Label>

                <TextareaAutosize
                  placeholder={`Enter a value for ${variable.name}...`}
                  value={variable.value}
                  onValueChange={(value) => {
                    const newPromptVariables = [...promptVariables]
                    newPromptVariables[index].value = value
                    setPromptVariables(newPromptVariables)
                  }}
                  minRows={3}
                  maxRows={5}
                  onCompositionStart={() => setIsTyping(true)}
                  onCompositionEnd={() => setIsTyping(false)}
                />
              </div>
            ))}
          </div>

          <div className="mt-2 flex justify-end space-x-2">
            <Button variant="ghost" size="sm" onClick={handleCancelPromptVariables}>
              Cancel
            </Button>

            <Button size="sm" onClick={handleSubmitPromptVariables}>
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {isPromptPickerOpen && (
        <div className="flex max-h-80 w-full min-w-0 flex-col space-y-1 overflow-y-auto p-2 text-sm">
          {/* Header with search info */}
          <div className="border-b px-2 py-1 text-xs text-gray-500">
            {filteredPrompts.length === 0
              ? slashCommand
                ? `No prompts match "${slashCommand}"`
                : 'No prompts available'
              : `Found ${filteredPrompts.length} prompt${filteredPrompts.length !== 1 ? 's' : ''}`}
          </div>

          {filteredPrompts.length === 0 ? (
            <div className="space-y-3 py-6 text-center">
              <div className="italic text-gray-500">
                {slashCommand ? `No prompts match "${slashCommand}"` : 'No prompts available'}
              </div>
              <Button
                onClick={handleCreatePrompt}
                variant="outline"
                size="sm"
                className="w-full justify-center hover:border-purple-300 hover:bg-purple-50 dark:hover:border-purple-700 dark:hover:bg-purple-950/30"
              >
                <IconPlus size={16} className="mr-2" />
                Create New Prompt
              </Button>
            </div>
          ) : (
            <>
              {filteredPrompts.map((prompt, index) => (
                <div
                  key={prompt.id}
                  ref={(ref) => {
                    itemsRef.current[index] = ref
                  }}
                  tabIndex={0}
                  className="hover:bg-accent focus:bg-accent dark:hover:border-muted-foreground/20 dark:focus:border-muted-foreground/20 flex cursor-pointer flex-col rounded-lg border border-transparent p-3 transition-colors hover:border-gray-200 focus:outline-none"
                  onClick={() => handleSelectPromptWithVariables(prompt)}
                  onKeyDown={getKeyDownHandler(index)}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <div className="dark:text-foreground truncate font-semibold text-gray-900">{prompt.name}</div>
                    {prompt.improvement_count > 0 && (
                      <div className="flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs text-purple-600 dark:bg-purple-950/50 dark:text-purple-300">
                        <IconSparkle2 size={12} />
                        {prompt.improvement_count}
                      </div>
                    )}
                  </div>

                  <div className="dark:text-muted-foreground line-clamp-2 text-sm leading-relaxed text-gray-600">
                    {prompt.content}
                  </div>

                  {prompt.change_summary && (
                    <div className="dark:text-muted-foreground/80 mt-1 truncate text-xs text-gray-500">
                      {prompt.change_summary}
                    </div>
                  )}
                </div>
              ))}

              {/* Always visible create button at the bottom */}
              <div className="mt-2 border-t pt-2">
                <Button
                  onClick={handleCreatePrompt}
                  variant="outline"
                  size="sm"
                  className="w-full justify-center hover:border-purple-300 hover:bg-purple-50 dark:hover:border-purple-700 dark:hover:bg-purple-950/30"
                >
                  <IconPlus size={16} className="mr-2" />
                  Create New Prompt
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}
